import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/shared/hooks/use-toast';
import { useTranslation } from '@/shared/i18n';
import { useImagePreview } from './useImagePreview';
import { usePresetNavigation } from './usePresetNavigation';
import { packApi } from '../api';
import {
  usePackPresets,
  useCreatePack,
  useUpdatePack,
  useRemovePreset,
} from '../model';
import type { Pack, CreatePackPayload, PackPreset, IPack } from '../types';
import type { CreatorWithPercentage } from '../model';
import { useQueryClient } from '@tanstack/react-query';
import { packKeys } from '../model/pack.keys';

type PackFormInitial = Partial<Pack> & { packsCreators?: CreatorWithPercentage[] };

export type UsePackFormOptions = {
  onSuccessCreate?: (data: IPack) => void | Promise<void>;
};

export function usePackForm(
  initialData?: PackFormInitial | null,
  getFormData?: () => {
    name: string;
    translations: Array<{ languageCode: string; description: string }>;
    creator: string;
    cover: string;
    published: boolean;
    creators: CreatorWithPercentage[];
  },
  selectedTags?: string[],
  options?: UsePackFormOptions
) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation('packs');
  const queryClient = useQueryClient();

  const [published, setPublished] = useState(initialData?.published ?? false);
  const [coverFile, setCoverFile] = useState<File | null>(null);

  const {
    imagePreview: coverPreview,
    setImagePreview: setCoverPreview,
    handleImageChange: _handleCoverChange,
    removeImagePreview: handleRemoveCover,
  } = useImagePreview({
    initialImage: initialData?.cover ?? undefined,
    maxSize: 500,
  });

  const { navigateToNewPreset, navigateToEditPreset } = usePresetNavigation();
  const { presets, refetch: refetchPresets } = usePackPresets(initialData?.id);
  const { createPack, isLoading: isCreating, mutationResult: createPackResult } = useCreatePack({
    onSuccess: async (data) => {
      await options?.onSuccessCreate?.(data);
      if (data?.id) navigate(`/packs/${data.id}`, { replace: true });
    },
    onError: () =>
      toast({
        title: t('errorCreating'),
        description: t('errorCreatingDescription'),
        variant: 'destructive',
      }),
  });
  const { updatePack, isLoading: isUpdating, mutationResult: updatePackResult } = useUpdatePack();
  const { removePreset } = useRemovePreset(initialData?.id);

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const submitting = isSubmittingForm || isCreating || isUpdating;

  useEffect(() => {
    if (initialData?.published !== undefined) setPublished(initialData.published);
  }, [initialData?.published]);

  useEffect(() => {
    if (initialData?.cover) setCoverPreview(initialData.cover);
  }, [initialData?.cover, setCoverPreview]);

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) setCoverFile(e.target.files[0]);
    _handleCoverChange(e);
  };

  const buildPayload = useCallback(
    (
      name: string,
      translations: Array<{ languageCode: string; description: string }>,
      creators: CreatorWithPercentage[]
    ): Omit<CreatePackPayload, 'cover'> => ({
      name,
      published,
      build: 1,
      firebaseId: null,
      displayOrder: initialData?.displayOrder ?? null,
      idApple: null,
      highlightOrder: initialData?.highlightOrder ?? null,
      translations: translations.filter((t) => t.description?.trim()).map((t) => ({
        languageCode: t.languageCode as 'PT_BR' | 'EN' | 'ES',
        description: t.description.trim(),
      })),
      creators: creators.map((c) => ({
        creatorId: c.id,
        percentage: c.percentage,
        displayOrder: c.displayOrder,
      })),
    }),
    [published, initialData?.displayOrder, initialData?.highlightOrder]
  );

  const handleSubmit = useCallback(
    async (
      e?: React.FormEvent<HTMLFormElement>,
      formDataOverride?: {
        name: string;
        translations: Array<{ languageCode: string; description: string }>;
        creators: CreatorWithPercentage[];
      }
    ) => {
      if (e) e.preventDefault();
      setIsSubmittingForm(true);
      try {
        let name = '';
        let creators: CreatorWithPercentage[] = [];

        let translations: Array<{ languageCode: string; description: string }> = [];

        if (formDataOverride) {
          name = formDataOverride.name;
          translations = formDataOverride.translations;
          creators = formDataOverride.creators;
        } else if (getFormData) {
          const data = getFormData();
          name = data.name;
          translations = data.translations;
          creators = data.creators;
        } else {
          const form = document.getElementById('pack-form') as HTMLFormElement | null;
          if (!form) return;
          const fd = new FormData(form);
          name = fd.get('name')?.toString() ?? '';
          const translationsJson = fd.get('translations')?.toString();
          translations = translationsJson ? JSON.parse(translationsJson) : [{ languageCode: 'PT_BR', description: '' }];
          creators =
            initialData?.packsCreators?.map((c) => ({
              id: c.id ?? c.creatorId ?? '',
              name: (c as CreatorWithPercentage).name ?? '',
              percentage: c.percentage ?? 0,
              displayOrder: c.displayOrder ?? 0,
            })) ?? [];
        }

        const payload = buildPayload(name, translations, creators);

        if (initialData?.id) {
          const packId = initialData.id;
          if (coverFile) {
            await packApi.uploadCover(packId, coverFile);
          }
          await updatePack(packId, payload);
          
          // Invalida o cache do pack para forçar reload dos dados atualizados
          queryClient.invalidateQueries({ queryKey: packKeys.detail(packId) });
          queryClient.invalidateQueries({ queryKey: packKeys.all });
        } else {
          if (!coverFile) {
            toast({
              title: t('requiredFields'),
              description: t('fixErrors'),
              variant: 'destructive',
            });
            return;
          }
          const pack = await createPack(payload);
          const packId = pack?.id;
          if (packId) {
            await packApi.uploadCover(packId, coverFile);
            await updatePack(packId, payload);
            
            // Invalida o cache do pack para forçar reload dos dados atualizados
            queryClient.invalidateQueries({ queryKey: packKeys.detail(packId) });
            queryClient.invalidateQueries({ queryKey: packKeys.all });
          }
        }
      } finally {
        setIsSubmittingForm(false);
      }
    },
    [
      getFormData,
      initialData?.id,
      initialData?.packsCreators,
      buildPayload,
      coverFile,
      coverPreview,
      updatePack,
      createPack,
      toast,
      t,
    ]
  );

  const handleAddPreset = () => {
    navigateToNewPreset(initialData?.id, initialData?.name);
  };

  const handleEditPreset = (preset: PackPreset) => {
    navigateToEditPreset(preset as PackPreset & { id: string }, initialData?.id, initialData?.name);
  };

  const handleRemovePresetFromList = async (presetId: string) => {
    try {
      await removePreset(presetId);
      refetchPresets();
    } catch {
      toast({
        title: t('errorRemovingPreset'),
        description: t('errorRemovingPresetDescription'),
        variant: 'destructive',
      });
    }
  };

  const handleRemoveCoverWithErrorClear = () => {
    setCoverFile(null);
    handleRemoveCover();
  };

  const dataUrlToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] ?? 'image/jpeg';
    const bstr = atob(arr[1]);
    const u8arr = new Uint8Array(bstr.length);
    for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
    return new File([u8arr], fileName, { type: mime });
  };

  const urlToFileViaCanvas = (url: string): Promise<File> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Canvas 2d não disponível'));
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error('toBlob falhou'));
              resolve(new File([blob], 'preset-cover.jpg', { type: 'image/jpeg' }));
            },
            'image/jpeg',
            0.9
          );
        } catch (e) {
          reject(e);
        }
      };
      img.onerror = () => reject(new Error('Imagem não carregou'));
      img.src = url;
    });

  const handleSetCoverFromUrl = async (coverUrl: string) => {
    try {
      setCoverPreview(coverUrl);
      setCoverFile(null);
      let file: File | null = null;
      if (coverUrl.startsWith('data:')) {
        file = dataUrlToFile(coverUrl, 'preset-cover.jpg');
      } else {
        try {
          const res = await fetch(coverUrl, { mode: 'cors', credentials: 'omit' });
          if (res.ok) {
            const blob = await res.blob();
            if (blob.size === 0) throw new Error('Resposta vazia');
            const base = coverUrl.split('/').pop()?.split('?')[0] ?? 'preset-cover';
            const name = base.includes('.') ? base : `${base}.jpg`;
            const type = blob.type?.startsWith('image/') ? blob.type : 'image/jpeg';
            file = new File([blob], name, { type });
          }
        } catch {
          try {
            file = await urlToFileViaCanvas(coverUrl);
          } catch {
            console.warn('Não foi possível converter URL em File.');
          }
        }
      }
      if (file) setCoverFile(file);
    } catch {
      toast({
        title: t('errorTitle'),
        description: t('errorSettingCover'),
        variant: 'destructive',
      });
    }
  };

  return {
    coverPreview,
    presets,
    submitting,
    published,
    setPublished,
    handleCoverChange,
    handleSubmit,
    handleAddPreset,
    handleRemovePreset: handleRemovePresetFromList,
    handleEditPreset,
    handleRemoveCover: handleRemoveCoverWithErrorClear,
    setCoverPreview,
    handleSetCoverFromUrl,
    createPackResult,
    updatePackResult,
  };
}
